<template>
  <!-- Landline: {{ landlineValidator }}<br />Mobile: {{ mobileValidator }} -->
  <Steps
    ref="formWizard"
    :steps="formSteps"
    :is-submitting="postNewCustomer.isLoading.value"
    @before-move="validateStep"
    @on-complete="submitNewCustomer"
  >
    <template #step-1>
      <FormGroup :label="$t('ui.entities.profile.companyName')">
        <Input
          v-model="newCustomer.companyName"
          icon="building"
        />
      </FormGroup>
      <div class="grid sm:grid-cols-3 sm:gap-8">
        <FormGroup
          :label="$t('ui.common.title')"
          :is-required="v$.title.required"
        >
          <Select
            v-model="newCustomer.title"
            mode="single"
            :close-on-select="true"
            :options="['Mr', 'Ms', 'Mrs', 'Miss', 'Dr']"
            :has-error="v$.title.$error"
          ></Select>
          <InputError :has-error="v$.title.$error">{{ v$.title.$errors[0]?.$message }}</InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.profile.firstName')"
          :is-required="v$.forename.required"
        >
          <Input
            v-model="newCustomer.forename"
            :has-error="v$.forename.$error"
          />
          <InputError :has-error="v$.forename.$error">
            {{ v$.forename.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.profile.lastName')"
          :is-required="v$.surname.required"
        >
          <Input
            v-model="newCustomer.surname"
            :has-error="v$.surname.$error"
          />
          <InputError :has-error="v$.surname.$error">
            {{ v$.surname.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
      <div class="grid sm:grid-cols-3 sm:gap-8">
        <FormGroup :label="$t('ui.entities.profile.dateOfBirth')">
          <DatePicker
            v-model="newCustomer.dateOfBirth"
            mode="single"
            display-as="input"
            display-format="full"
            :has-error="v$.dateOfBirth.$error"
          />
          <InputError :has-error="v$.dateOfBirth.$error">
            {{ v$.dateOfBirth.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </template>

    <template #step-2>
      <FormGroup
        :label="$t('ui.entities.address.line1')"
        :is-required="v$.address.line1.required?.$invalid"
      >
        <Input
          v-model="newCustomer.address.line1"
          :has-error="v$.address.line1.$error"
        />
        <InputError :has-error="v$.address.line1.$error">
          {{ v$.address.line1.$errors[0]?.$message }}
        </InputError>
      </FormGroup>
      <FormGroup
        :label="$t('ui.entities.address.line2')"
        :is-required="v$.address.line2.required?.$invalid"
      >
        <Input
          v-model="newCustomer.address.line2"
          :has-error="v$.address.line2.$error"
        />
        <InputError :has-error="v$.address.line2.$error">
          {{ v$.address.line2.$errors[0]?.$message }}
        </InputError>
      </FormGroup>
      <div class="grid sm:grid-cols-2 sm:gap-8">
        <FormGroup
          :label="$t('ui.entities.address.city')"
          :is-required="v$.address.city.required?.$invalid"
        >
          <Input
            v-model="newCustomer.address.city"
            :has-error="v$.address.city.$error"
          />
          <InputError :has-error="v$.address.city.$error">
            {{ v$.address.city.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.address.county')"
          :is-required="v$.address.county.required?.$invalid"
        >
          <Input
            v-model="newCustomer.address.county"
            :has-error="v$.address.county.$error"
          />
          <InputError :has-error="v$.address.county.$error">
            {{ v$.address.county.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
      <div class="grid sm:grid-cols-2 sm:gap-8">
        <FormGroup
          :label="$t('ui.entities.address.postCode')"
          :is-required="v$.address.postCode.required?.$invalid"
        >
          <Input
            v-model="newCustomer.address.postCode"
            :has-error="v$.address.postCode.$error"
          />
          <InputError :has-error="v$.address.postCode.$error">
            {{ v$.address.postCode.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.address.country')"
          :is-required="v$.address.country.required"
        >
          <Select
            v-model="newCustomer.address.country"
            mode="single"
            :close-on-select="true"
            handle-search
            :options="[
              {
                name: 'United Kingdom',
                code: 'GB',
                image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/GB.svg'
              },
              {
                name: 'United States',
                code: 'US',
                image: 'https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/US.svg'
              }
            ]"
            :is-object="true"
            :has-error="v$.address.country.$error"
            value-key="code"
            label-key="name"
            value-prop="code"
          >
            <template #value="{ value }">
              <div class="flex items-center">
                <img
                  :src="value.image"
                  width="25"
                  class="mr-3"
                />
                {{ value.name }}
              </div>
            </template>
            <template #option="{ option }">
              <div class="flex">
                <img
                  :src="option.image"
                  width="25"
                  class="mr-3"
                />
                {{ option.name }}
              </div>
            </template>
          </Select>
          <InputError :has-error="v$.address.country.$error">
            {{ v$.address.country.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </template>

    <template #step-3>
      <div class="grid sm:grid-cols-2 sm:gap-4">
        <FormGroup :label="$t('ui.entities.comms.landline')">
          <InputPhone
            v-model="newCustomer.landlineString"
            @update:number-details="newCustomer.landlineObj = $event"
          />
          <InputError :has-error="v$.landlineString.$error">
            {{ v$.landlineString.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup :label="$t('ui.entities.comms.mobile')">
          <InputPhone
            v-model="newCustomer.mobileString"
            @update:number-details="newCustomer.mobileObj = $event"
          />
          <InputError :has-error="v$.mobileString.$error">
            {{ v$.mobileString.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
      <FormGroup :label="$t('ui.entities.comms.email')">
        <Input
          v-model="newCustomer.email"
          icon="at"
        />
        <InputError :has-error="v$.email.$error">{{ v$.email.$errors[0]?.$message }}</InputError>
      </FormGroup>
      <div>
        <h2 class="mb-2">{{ $t('ui.entities.comms.permissions.label') }}</h2>
        <Checkbox
          v-model="newCustomer.contactPermissions.canPhone"
          :label="$t('ui.entities.comms.permissions.call')"
        ></Checkbox>
        <Checkbox
          v-model="newCustomer.contactPermissions.canEmail"
          :label="$t('ui.entities.comms.permissions.email')"
        ></Checkbox>
        <Checkbox
          v-model="newCustomer.contactPermissions.canSMS"
          :label="$t('ui.entities.comms.permissions.sms')"
        ></Checkbox>
        <Checkbox
          v-model="newCustomer.contactPermissions.canMail"
          :label="$t('ui.entities.comms.permissions.mail')"
        ></Checkbox>
      </div>
    </template>

    <template #step-4>
      <div class="max-w-[600px]">
        <FormGroup
          :label="$t('ui.entities.vehicle.vrm')"
          class="mb-6"
        >
          <div class="grid md:grid-cols-3 md:gap-x-4">
            <Input
              v-model="newCustomer.vehicle.vrm"
              :has-error="v$.vehicle.vrm.$error"
            />
            <Button
              label="Get Vehicle Details"
              outlined
              :disabled="!newCustomer.vehicle.vrm || dvlaEnquiryRequest.isLoading.value"
              :is-loading="dvlaEnquiryRequest.isLoading.value"
              @click="fetchVehicleDetailsFromDVLA"
            />
          </div>
          <InputError :has-error="v$.vehicle.vrm.$error">
            {{ v$.vehicle.vrm.$errors[0]?.$message }}
          </InputError>
        </FormGroup>

        <div class="grid md:grid-cols-3 md:gap-x-4">
          <FormGroup
            :label="$t('ui.entities.vehicle.make')"
            :is-required="v$.vehicle.make.required"
          >
            <Select
              v-model="newCustomer.vehicle.make"
              mode="single"
              :close-on-select="true"
              searchable
              handle-search
              :options="manufacturers.data.value"
              on-select-return-as-object
              :has-error="v$.vehicle.make.$error"
              value-key="name"
              label-key="name"
              value-prop="name"
            >
              <template #value="{ value }">
                <div class="flex items-center">
                  <img
                    :src="value.squareLogoUrl"
                    style="width: 20px; max-height: 20px"
                    class="mr-3"
                  />
                  {{ value.name }}
                </div>
              </template>
              <template #option="{ option }">
                <div class="flex">
                  <img
                    :src="option.squareLogoUrl"
                    style="width: 20px; max-height: 20px"
                    class="mr-3"
                  />
                  {{ option.name }}
                </div>
              </template>
            </Select>
            <InputError :has-error="v$.vehicle.make.$error">
              {{ v$.vehicle.make.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup
            :label="$t('ui.entities.vehicle.model')"
            :is-required="v$.vehicle.model.required"
          >
            <Select
              v-model="newCustomer.vehicle.model"
              mode="single"
              :close-on-select="true"
              searchable
              handle-search
              :options="vehicleModels.data.value || []"
              :has-error="v$.vehicle.model.$error"
              :readonly="!newCustomer.vehicle.make"
              label-key="model"
              value-key="model"
            />
            <InputError :has-error="v$.vehicle.model.$error">
              {{ v$.vehicle.model.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup
            :label="$t('ui.entities.vehicle.derivative')"
            :is-required="v$.vehicle.derivative.required"
          >
            <Input
              v-model="newCustomer.vehicle.derivative"
              :has-error="v$.vehicle.derivative.$error"
            />
            <InputError :has-error="v$.vehicle.derivative.$error">
              {{ v$.vehicle.derivative.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
        </div>

        <div class="grid md:grid-cols-3 md:gap-x-4">
          <FormGroup label="Fuel Type">
            <Select
              v-model="newCustomer.vehicle.fuelType"
              mode="single"
              :close-on-select="true"
              searchable
              :options="fuelTypeOptions"
              label-key="label"
              value-key="value"
              :has-error="v$.vehicle.fuelType.$error"
            />
            <InputError :has-error="v$.vehicle.fuelType.$error">
              {{ v$.vehicle.fuelType.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup label="Color">
            <Select
              v-model="newCustomer.vehicle.color"
              mode="single"
              :close-on-select="true"
              searchable
              handle-search
              :options="colorOptions"
              :has-error="v$.vehicle.color.$error"
            />
            <InputError :has-error="v$.vehicle.color.$error">
              {{ v$.vehicle.color.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup label="Doors">
            <RadioTabbed
              v-model="newCustomer.vehicle.doors"
              :options="doorsOptions"
              :has-error="v$.vehicle.doors.$error"
            />
            <InputError :has-error="v$.vehicle.doors.$error">
              {{ v$.vehicle.doors.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
        </div>

        <div class="grid md:grid-cols-3 md:gap-x-4">
          <FormGroup
            :label="$t('ui.forms.modelYear')"
            :is-required="v$.vehicle.modelYear.required"
          >
            <Input
              v-model="newCustomer.vehicle.modelYear"
              :has-error="v$.vehicle.modelYear.$error"
              :placeholder="$t('ui.forms.modelYearPlaceholder')"
            />
            <InputError :has-error="v$.vehicle.modelYear.$error">
              {{ v$.vehicle.modelYear.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup :label="$t('ui.entities.vehicle.regDate')">
            <DatePicker
              v-model="newCustomer.vehicle.regDate"
              mode="single"
              display-as="input"
              display-format="full"
              :has-error="v$.vehicle.regDate.$error"
            />
            <InputError :has-error="v$.vehicle.regDate.$error">
              {{ v$.vehicle.regDate.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup
            :label="$t('ui.entities.vehicle.newUsed')"
            :is-required="v$.vehicle.newUsed.required"
          >
            <RadioTabbed
              v-model="newCustomer.vehicle.newUsed"
              :options="[
                { label: 'New', value: 'new' },
                { label: 'Used', value: 'used' }
              ]"
              :selected-option="newCustomer.vehicle.newUsed"
              :has-error="v$.vehicle.newUsed.$error"
            />
            <InputError :has-error="v$.vehicle.newUsed.$error">
              {{ v$.vehicle.newUsed.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
        </div>

        <div class="grid md:grid-cols-3 md:gap-x-4">
          <FormGroup
            :label="$t('ui.entities.vehicle.vin')"
            class="md:col-span-2"
            :is-required="v$.vehicle.vin.required"
          >
            <Input
              v-model="newCustomer.vehicle.vin"
              :has-error="v$.vehicle.vin.$error"
            />
            <InputError :has-error="v$.vehicle.vin.$error">
              {{ v$.vehicle.vin.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
        </div>
      </div>
    </template>

    <template #step-5>
      <div class="grid sm:grid-cols-2 sm:gap-4">
        <FormGroup
          :label="$t('ui.entities.import.importSource')"
          info="The source of the data you are adding"
          :is-required="v$.importInfo.source.required"
        >
          <Select
            v-model="newCustomer.importInfo.source"
            mode="single"
            :close-on-select="true"
            :handle-search="false"
            :options="[
              { label: 'Key2Key', value: 'k2k' },
              { label: 'P360', value: 'p360' },
              { label: 'Blackhorse', value: 'blackhorse' },
              { label: 'Leap (Ford)', value: 'leap' },
              { label: 'eMaster', value: 'emaster' }
            ]"
            :has-error="v$.importInfo.source.$error"
          ></Select>
          <InputError :has-error="v$.importInfo.source.$error">
            {{ v$.importInfo.source.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
      <div class="grid sm:grid-cols-2 sm:gap-4">
        <FormGroup
          :label="$t('ui.entities.dealership')"
          :is-required="v$.importInfo.dealershipID.required"
        >
          <Select
            v-model="newCustomer.importInfo.dealershipID"
            mode="single"
            searchable
            :options="dealerships.data.value"
            :has-error="v$.importInfo.dealershipID.$error"
            label-key="name"
            value-key="id"
            :dropdown-match-width="false"
          ></Select>
          <InputError :has-error="v$.importInfo.dealershipID.$error">
            {{ v$.importInfo.dealershipID.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.financePlanNumber')"
          info="Found on 3rd party finance system"
          :is-required="v$.importInfo.financePlanNumber.required"
        >
          <Input
            v-model="newCustomer.importInfo.financePlanNumber"
            :has-error="v$.importInfo.financePlanNumber.$error"
          />
          <InputError :has-error="v$.importInfo.financePlanNumber.$error">
            {{ v$.importInfo.financePlanNumber.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </template>

    <template #step-6>
      <div class="grid sm:grid-cols-3 sm:gap-4">
        <FormGroup
          :label="$t('ui.entities.financeAgreement.financeProduct')"
          :is-required="v$.agreement.productType.required"
        >
          <RadioTabbed
            v-model="newCustomer.agreement.productType"
            :options="[
              { label: 'PCP', value: 'PCP' },
              { label: 'HP', value: 'HP' },
              { label: 'CH', value: 'CH' }
            ]"
            :has-error="v$.agreement.productType.$error"
            :selected-option="newCustomer.agreement.productType"
          />
          <InputError :has-error="v$.agreement.productType.$error">
            {{ v$.agreement.productType.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.financeProductName')"
          info="Name of the product (ie. Selekt)"
          :is-required="v$.agreement.productName.required"
        >
          <Input
            v-model="newCustomer.agreement.productName"
            :has-error="v$.agreement.productName.$error"
          />
          <InputError :has-error="v$.agreement.productName.$error">
            {{ v$.agreement.productName.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.common.currency')"
          :is-required="v$.agreement.currency.required"
        >
          <Select
            v-model="newCustomer.agreement.currency"
            mode="single"
            :close-on-select="true"
            :options="['GBP', 'USD', 'EUR']"
            :has-error="v$.agreement.currency.$error"
          ></Select>
          <InputError :has-error="v$.agreement.currency.$error">
            {{ v$.agreement.currency.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.rate')"
          :is-required="v$.agreement.rate.required"
        >
          <Input
            v-model="newCustomer.agreement.rate"
            icon="percent"
            :has-error="v$.agreement.rate.$error"
          />
          <InputError :has-error="v$.agreement.rate.$error">
            {{ v$.agreement.rate.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.term')"
          :is-required="v$.agreement.term.required"
        >
          <Input
            v-model="newCustomer.agreement.term"
            :has-error="v$.agreement.term.$error"
          />
          <InputError :has-error="v$.agreement.term.$error">
            {{ v$.agreement.term.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.deposit')"
          :is-required="v$.agreement.deposit.required"
        >
          <Input
            v-model="newCustomer.agreement.deposit"
            icon="gbp"
            :has-error="v$.agreement.deposit.$error"
          />
          <InputError :has-error="v$.agreement.deposit.$error">
            {{ v$.agreement.deposit.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.startDate')"
          :is-required="v$.agreement.startDate.required"
        >
          <DatePicker
            v-model="newCustomer.agreement.startDate"
            mode="single"
            display-as="input"
            display-format="full"
            :has-error="v$.agreement.startDate.$error"
          />
          <InputError :has-error="v$.agreement.startDate.$error">
            {{ v$.agreement.startDate.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.endDate')"
          :is-required="v$.agreement.endDate.required"
        >
          <DatePicker
            v-model="newCustomer.agreement.endDate"
            mode="single"
            display-as="input"
            display-format="full"
            :has-error="v$.agreement.endDate.$error"
          />
          <InputError :has-error="v$.agreement.endDate.$error">
            {{ v$.agreement.endDate.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.monthlyPayment')"
          :is-required="v$.agreement.monthlyPayment.required"
        >
          <Input
            v-model="newCustomer.agreement.monthlyPayment"
            icon="gbp"
            :has-error="v$.agreement.monthlyPayment.$error"
          />
          <InputError :has-error="v$.agreement.monthlyPayment.$error">
            {{ v$.agreement.monthlyPayment.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.purchasePrice')"
          :is-required="v$.agreement.purchasePrice.required"
        >
          <Input
            v-model="newCustomer.agreement.purchasePrice"
            icon="gbp"
            :has-error="v$.agreement.purchasePrice.$error"
          />
          <InputError :has-error="v$.agreement.purchasePrice.$error">
            {{ v$.agreement.purchasePrice.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.amountFinanced')"
          :is-required="v$.agreement.amountBorrowed.required"
        >
          <Input
            v-model="newCustomer.agreement.amountBorrowed"
            icon="gbp"
            :has-error="v$.agreement.amountBorrowed.$error"
          />
          <InputError :has-error="v$.agreement.amountBorrowed.$error">
            {{ v$.agreement.amountBorrowed.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          v-if="newCustomer.agreement.productType == 'PCP'"
          :label="$t('ui.entities.financeAgreement.finalRepayment')"
          :is-required="v$.agreement.finalRepayment.required"
        >
          <Input
            v-model="newCustomer.agreement.finalRepayment"
            icon="gbp"
            :has-error="v$.agreement.finalRepayment.$error"
          />
          <InputError :has-error="v$.agreement.finalRepayment.$error">
            {{ v$.agreement.finalRepayment.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.annualDistanceAllowance')"
          :is-required="v$.agreement.odometer.annualAllowance.required"
        >
          <Input
            v-model="newCustomer.agreement.odometer.annualAllowance"
            icon="gauge"
            :has-error="v$.agreement.odometer.annualAllowance.$error"
          />
          <InputError :has-error="v$.agreement.odometer.annualAllowance.$error">
            {{ v$.agreement.odometer.annualAllowance.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
        <FormGroup
          :label="$t('ui.entities.financeAgreement.distanceUnit')"
          :is-required="v$.agreement.odometer.distanceUnit.required"
        >
          <RadioTabbed
            v-model="newCustomer.agreement.odometer.distanceUnit"
            :options="[
              { label: 'mi', value: 'miles' },
              { label: 'km', value: 'kilometers' }
            ]"
            :has-error="v$.agreement.odometer.distanceUnit.$error"
            :selected-option="newCustomer.agreement.odometer.distanceUnit"
          />
          <InputError :has-error="v$.agreement.odometer.distanceUnit.$error">
            {{ v$.agreement.odometer.distanceUnit.$errors[0]?.$message }}
          </InputError>
        </FormGroup>
      </div>
    </template>
  </Steps>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Steps from '@/components/steps/Steps.vue'

import dayjs from 'dayjs'

import FormGroup from '@/components/forms/FormGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputPhone from '@/components/forms/InputPhone.vue'
import Checkbox from '@/components/forms/Checkbox.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import InputError from '@/components/forms/InputError.vue'
import Select from '@/components/forms/Select.vue'
import Button from '@/components/button/Button.vue'

import DatePicker from '@/components/date-picker/DatePicker.vue'

import useVuelidate from '@vuelidate/core'
import { required, requiredIf, email, integer, decimal, helpers } from '@vuelidate/validators'

import useApiRequest from '@/composables/useApiRequest'
import { useAccessStore } from '@/stores/AccessStore'

import { alertToast } from '@/utilities/notification'

const emit = defineEmits(['form-updated'])
const { t } = useI18n()
const router = useRouter()
const accessStore = useAccessStore()

const manufacturers = useApiRequest()
const dealerships = useApiRequest()
const vehicleModels = useApiRequest()
const postNewCustomer = useApiRequest()
const dvlaEnquiryRequest = useApiRequest()

const fuelTypeOptions = ref([
  { label: 'Electricity', value: 'ELECTRICITY' },
  { label: 'Petrol', value: 'PETROL' },
  { label: 'Diesel', value: 'DIESEL' },
  { label: 'Hybrid Electric', value: 'HYBRID ELECTRIC' }
])

const doorsOptions = ref([
  { label: '2dr', value: 2 },
  { label: '5dr', value: 5 }
])

const colorOptions = ref([
  'Black',
  'White',
  'Silver',
  'Grey',
  'Red',
  'Blue',
  'Green',
  'Yellow',
  'Orange',
  'Brown',
  'Beige',
  'Gold',
  'Purple',
  'Pink',
  'Bronze',
  'Copper'
])

manufacturers.send({ endpoint: '/v1/manufacturers?showDisabled=true' })
const dealershipsAllowed = accessStore.accessAllowed.dealerships.map(dealership => dealership.id)

dealerships.send({ endpoint: '/v1/dealerships', params: { ids: dealershipsAllowed.join(',') } })

const formWizard = ref(null) // Reference to the FormWizard component

const formSteps = computed(() => [
  {
    label: t('ui.forms.basicDetails'),
    title: t('ui.forms.theirBasicDetails'),
    icon: null
  },
  {
    label: t('ui.entities.address.label'),
    title: t('ui.forms.whatIsTheirAddress'),
    icon: null
  },
  {
    label: t('ui.forms.contactDetails'),
    title: t('ui.forms.howCanWeContactThem'),
    icon: null
  },
  {
    label: t('ui.forms.vehicleDetails'),
    title: t('ui.forms.whatVehicleDoTheyHave'),
    icon: null
  },
  {
    label: t('ui.entities.agreement.label'),
    title: t('ui.forms.whatIsTheirAgreement'),
    icon: null
  },
  {
    label: t('ui.forms.summary'),
    title: t('ui.forms.summary'),
    icon: null
  }
])

const newCustomer = ref({
  companyName: null,
  title: null,
  forename: null,
  surname: null,
  dateOfBirth: null,
  address: {
    line1: null,
    line2: null,
    city: null,
    county: null,
    postCode: null,
    country: null
  },
  landlineString: '',
  landlineObj: null,
  mobileString: '',
  mobileObj: null,
  email: '',
  contactPermissions: {
    canPhone: false,
    canEmail: false,
    canSMS: false,
    canMail: false
  },
  vehicle: {
    make: null,
    model: null,
    derivative: null,
    modelYear: null,
    vin: null,
    vrm: null,
    newUsed: null,
    regDate: null,
    fuelType: null,
    doors: null,
    color: null
  },
  importInfo: {
    dealershipID: null,
    source: null,
    financePlanNumber: null
  },
  agreement: {
    financePlanNumber: null,
    currency: null,
    productType: null,
    productName: null,
    startDate: null,
    endDate: null,
    term: null,
    rate: null,
    rateType: 'APR',
    deposit: null,
    purchasePrice: null,
    odometer: {
      annualAllowance: null,
      distanceUnit: null
    },
    monthlyPayment: null,
    equity: null,
    amountBorrowed: null,
    finalRepayment: null
  }
})

const inputRules = {
  formStep1: {
    companyName: {},
    title: {},
    forename: {
      required: helpers.withMessage(
        'Required if no company name',
        requiredIf(function () {
          return !newCustomer.value.companyName
        })
      )
    },
    surname: {
      required: helpers.withMessage(
        'Required if no company name',
        requiredIf(function () {
          return !newCustomer.value.companyName
        })
      )
    },
    dateOfBirth: {
      required: false,
      dateIsValid: helpers.withMessage('Not a valid date', value => {
        return value != null ? dayjs(value).isValid() : true
      }),
      dateInFuture: helpers.withMessage('Must be in the past', value => {
        return value != null ? dayjs(value).isBefore(dayjs()) : true
      })
    }
  },
  formStep2: {
    address: {
      line1: {
        required: requiredIf(function () {
          return newCustomer.value.address.postCode
        })
      },
      line2: {},
      city: {
        required: requiredIf(function () {
          return newCustomer.value.address.postCode
        })
      },
      county: {
        required: requiredIf(function () {
          return newCustomer.value.address.postCode
        })
      },
      postCode: {
        required: requiredIf(function () {
          return newCustomer.value.address.line1
        })
      },
      country: { required }
    }
  },
  formStep3: {
    landlineString: {
      validNumber: helpers.withMessage('Invalid Number', () => {
        // Otherwise, return true (meaning no error for this rule)
        return true
      }),
      isLandline: helpers.withMessage('Not a landline number', () => {
        return true
      })
    },
    mobileString: {
      validNumber: helpers.withMessage('Invalid Number', () => {
        return true
      }),
      isMobile: helpers.withMessage('Not a mobile number', () => {
        return true
      })
    },
    email: { email },
    contactPermissions: {
      canPhone: {
        required
      },
      canEmail: {
        required
      },
      canSMS: {
        required
      },
      canMail: {
        required
      }
    }
  },
  formStep4: {
    vehicle: {
      make: { required },
      model: { required },
      derivative: {},
      modelYear: {
        required,
        valid: helpers.withMessage('Invalid Model Year', value => {
          // Must be 4 digits
          return value != null ? value.length == 4 : true
        })
      },
      vin: {
        required
        // valid: helpers.withMessage('Invalid VIN', helpers.regex(/\b[(A-H|J-N|P|R-Z|0-9)]{17}\b/))
      },
      vrm: { required },
      newUsed: { required },
      regDate: { required },
      fuelType: {},
      doors: {},
      color: {}
    }
  },
  formStep5: {
    importInfo: {
      dealershipID: { required },
      source: { required },
      financePlanNumber: {
        required,
        validFormat: helpers.withMessage('Invalid Format', value => {
          let regex
          switch (newCustomer.value.importInfo.source) {
            case 'k2k':
              regex = /^000044\d{10}$/
              break
            case 'p360':
              regex = /^\d+$/
              break
            default:
              regex = /^.{5,}$/
              break
          }

          return regex.test(value)
        })
      }
    }
  },
  formStep6: {
    agreement: {
      currency: { required },
      productType: { required },
      productName: {},
      startDate: { required },
      endDate: { required },
      term: { required, integer },
      rate: { decimal },
      rateType: {},
      deposit: { decimal },
      purchasePrice: { required, decimal },
      odometer: {
        annualAllowance: { required, integer },
        distanceUnit: { required }
      },
      monthlyPayment: { required, decimal },
      amountBorrowed: { required, decimal },
      finalRepayment: {
        required: helpers.withMessage(
          'Required if product type is PCP',
          requiredIf(function () {
            return newCustomer.value.agreement.productType == 'PCP'
          })
        ),
        decimal
      }
    }
  }
}

watch(
  () => newCustomer.value,
  () => {
    emit('form-updated')
  },
  { deep: true }
)

/**
 * Watch for changes to the vehicle make and fetch models when it changes
 */
watch(
  () => newCustomer.value.vehicle.make,
  newMake => {
    // Reset model when make changes
    newCustomer.value.vehicle.model = null
    // Clear model options immediately
    vehicleModels.data.value = []

    // Fetch models if a make is selected
    if (newMake && newMake.id) {
      vehicleModels.send({
        endpoint: '/v1/vehicles/models',
        params: { manufacturerId: newMake.id, showLegacyModels: 'true' }
      })
    }
  }
)

/**
 * Watch for vehicle models to load and ensure current model is in the list
 * This prevents data loss for rare models not in our database
 */
watch(
  () => vehicleModels.data.value,
  models => {
    if (models && Array.isArray(models) && newCustomer.value.vehicle.model) {
      const currentModel = newCustomer.value.vehicle.model
      // API returns models as an array of objects with a 'model' property
      const modelExists = models.some(model => {
        return typeof model === 'object' && model?.model === currentModel
      })

      // If current model is not in the list, add it as an option
      if (!modelExists && currentModel) {
        vehicleModels.data.value = [...models, { model: currentModel }]
      }
    }
  }
)

/**
 * Ensures the current color value is in the color options list
 * This prevents data loss for custom colors not in our predefined list
 */
function ensureColorInList() {
  if (newCustomer.value?.vehicle?.color && colorOptions.value) {
    const currentColor = newCustomer.value.vehicle.color
    const colorExists = colorOptions.value.includes(currentColor)

    // If current color is not in the list, add it as an option
    if (!colorExists && currentColor) {
      colorOptions.value = [...colorOptions.value, currentColor]
    }
  }
}

/**
 * Watch for color changes and ensure it's in the options list
 */
watch(
  () => newCustomer.value?.vehicle?.color,
  () => {
    ensureColorInList()
  }
)

const stepValidationRules = computed(() => {
  // console.log('stepValidationRules ' + formWizard.value?.currentStep)
  if (formWizard.value?.currentStep) {
    // console.log(`formStep${formWizard.value?.currentStep}`)
    return inputRules[`formStep${formWizard.value?.currentStep + 1}`]
  }
  return inputRules.formStep1
})

const v$ = useVuelidate(stepValidationRules, newCustomer)

/**
 * Fetches vehicle details from DVLA API based on registration number
 */
async function fetchVehicleDetailsFromDVLA() {
  if (!newCustomer.value?.vehicle?.vrm) {
    alertToast('Please enter a registration number', null, 'error')
    return
  }

  const registrationNumber = newCustomer.value.vehicle.vrm.trim().toUpperCase()

  // Clear all vehicle data fields before fetching new details
  // This prevents leftover data from a previous incorrect VRM
  newCustomer.value.vehicle.make = null
  newCustomer.value.vehicle.model = null
  newCustomer.value.vehicle.derivative = null
  newCustomer.value.vehicle.fuelType = null
  newCustomer.value.vehicle.color = null
  newCustomer.value.vehicle.doors = null
  newCustomer.value.vehicle.modelYear = null
  newCustomer.value.vehicle.regDate = null
  newCustomer.value.vehicle.newUsed = null
  newCustomer.value.vehicle.vin = null
  // Clear vehicle models list
  vehicleModels.data.value = []

  dvlaEnquiryRequest
    .send({
      endpoint: '/v1/dvla/enquiry',
      method: 'GET',
      params: { registrationNumber }
    })
    .then(response => {
      if (response.success && response.data) {
        const data = response.data

        // Populate available fields (excluding model and derivative as per requirements)
        if (data.make && manufacturers.data.value) {
          // Match manufacturer case-insensitively since DVLA returns uppercase
          const manufacturer = manufacturers.data.value.find(m => m.name.toUpperCase() === data.make.toUpperCase())
          if (manufacturer) {
            newCustomer.value.vehicle.make = manufacturer
            // Fetch models for the selected make
            vehicleModels.send({
              endpoint: '/v1/vehicles/models',
              params: { manufacturerId: manufacturer.id, showLegacyModels: 'true' }
            })
          } else {
            // If no match found, try to find a partial match or use the string as-is
            newCustomer.value.vehicle.make = data.make
          }
        }

        // Handle color field (check both 'color' and 'colour' for API compatibility)
        const colorValue = (data.color || data.colour)?.trim()
        if (colorValue && colorOptions.value) {
          // Match color case-insensitively since DVLA returns capitalized
          const matchingColor = colorOptions.value.find(c => c.toUpperCase() === colorValue.toUpperCase())
          if (matchingColor) {
            newCustomer.value.vehicle.color = matchingColor
            console.info('Color matched:', matchingColor, 'from API value:', colorValue)
          } else {
            // If no match found, add it to the options list
            newCustomer.value.vehicle.color = colorValue
            ensureColorInList()
            console.info('Color added to options:', colorValue)
          }
        } else {
          console.info(
            'Color not set - colorValue:',
            colorValue,
            'colorOptions available:',
            !!colorOptions.value,
            'API data:',
            data
          )
        }

        if (data.fuelType) {
          // Map DVLA fuel type to our format if needed
          const fuelTypeMapping = {
            ELECTRIC: 'ELECTRICITY',
            HYBRID: 'HYBRID ELECTRIC'
          }
          newCustomer.value.vehicle.fuelType = fuelTypeMapping[data.fuelType] || data.fuelType
        }

        if (data.monthOfFirstRegistration) {
          // Convert month/year to YYYY-MM-DD format
          // DVLA returns format like "YYYY-MM" or might return full date
          let regDateValue = data.monthOfFirstRegistration

          // If it's in YYYY-MM format, use the first day of that month
          if (typeof regDateValue === 'string' && regDateValue.match(/^\d{4}-\d{2}$/)) {
            regDateValue = regDateValue + '-01'
          }

          // Parse and format to YYYY-MM-DD
          const parsedDate = dayjs(regDateValue)
          if (parsedDate.isValid()) {
            newCustomer.value.vehicle.regDate = parsedDate.format('YYYY-MM-DD')
          } else {
            // If parsing fails, try to extract date from various formats
            const fallbackDate = dayjs(regDateValue, ['YYYY-MM-DD', 'YYYY-MM', 'DD/MM/YYYY', 'MM/DD/YYYY'], true)
            if (fallbackDate.isValid()) {
              newCustomer.value.vehicle.regDate = fallbackDate.format('YYYY-MM-DD')
            }
          }
        }

        alertToast('Vehicle details retrieved successfully', null, 'success')
      } else {
        alertToast('Failed to retrieve vehicle details', response.message || 'Unknown error', 'error')
      }
    })
    .catch(error => {
      alertToast('Failed to retrieve vehicle details', error.message || 'Unknown error', 'error')
    })
}

async function validateStep() {
  // console.log('Step: ' + step)
  const isStepCorrect = await v$.value.$validate()
  // console.log('Is Valid: ' + isStepCorrect)
  if (isStepCorrect) {
    formWizard.value.move(1) // Move to the next step
  }
}

/**
 * Normalizes input values to a trimmed string or null.
 */
function toNullableString(value) {
  if (value === null || value === undefined) {
    return null
  }

  const normalizedValue = String(value).trim()
  return normalizedValue ? normalizedValue : null
}

/**
 * Converts form values to integers when present.
 */
function toNullableInteger(value) {
  const normalizedValue = toNullableString(value)

  if (!normalizedValue) {
    return null
  }

  const parsedValue = Number.parseInt(normalizedValue, 10)
  return Number.isNaN(parsedValue) ? null : parsedValue
}

/**
 * Converts form values to numbers when present.
 */
function toNullableNumber(value) {
  const normalizedValue = toNullableString(value)

  if (!normalizedValue) {
    return null
  }

  const parsedValue = Number(normalizedValue)
  return Number.isNaN(parsedValue) ? null : parsedValue
}

/**
 * Builds the payload format expected by the customer endpoint for phone numbers.
 */
function buildPhonePayload(phoneDetails) {
  const number = toNullableString(phoneDetails?.number)

  return {
    countryCode: number ? toNullableString(phoneDetails?.countryCode) : null,
    number
  }
}

async function submitNewCustomer() {
  const isFormCorrect = await v$.value.$validate()
  // console.log('FormCorrect:' + isFormCorrect, v$.value)

  // Timezone should be same as dealership's timezone
  const timezone = dealerships.data.value.find(
    dealership => dealership.id == newCustomer.value.importInfo.dealershipID
  )?.timeZone

  if (isFormCorrect) {
    const addressCountryCode = toNullableString(
      newCustomer.value.address.country?.code || newCustomer.value.address.country
    )
    const vehicleMake = toNullableString(newCustomer.value.vehicle.make?.name || newCustomer.value.vehicle.make)
    const dataToPost = {
      basicDetails: {
        companyName: toNullableString(newCustomer.value.companyName),
        title: toNullableString(newCustomer.value.title),
        forename: toNullableString(newCustomer.value.forename),
        surname: toNullableString(newCustomer.value.surname),
        dateOfBirth: toNullableString(newCustomer.value.dateOfBirth)
      },
      address: {
        line1: toNullableString(newCustomer.value.address.line1),
        line2: toNullableString(newCustomer.value.address.line2),
        city: toNullableString(newCustomer.value.address.city),
        county: toNullableString(newCustomer.value.address.county),
        postCode: toNullableString(newCustomer.value.address.postCode),
        country: addressCountryCode
      },
      contactDetails: {
        email: toNullableString(newCustomer.value.email),
        landline: buildPhonePayload(newCustomer.value.landlineObj),
        mobile: buildPhonePayload(newCustomer.value.mobileObj),
        permissions: {
          canPhone: newCustomer.value.contactPermissions.canPhone,
          canEmail: newCustomer.value.contactPermissions.canEmail,
          canSMS: newCustomer.value.contactPermissions.canSMS,
          canMail: newCustomer.value.contactPermissions.canMail
        },
        timeZone: toNullableString(timezone)
      },
      vehicle: {
        make: vehicleMake,
        model: toNullableString(newCustomer.value.vehicle.model),
        derivative: toNullableString(newCustomer.value.vehicle.derivative),
        vin: toNullableString(newCustomer.value.vehicle.vin),
        vrm: toNullableString(newCustomer.value.vehicle.vrm),
        newUsed: toNullableString(newCustomer.value.vehicle.newUsed),
        regDate: toNullableString(newCustomer.value.vehicle.regDate),
        color: toNullableString(newCustomer.value.vehicle.color),
        doors: toNullableInteger(newCustomer.value.vehicle.doors),
        modelYear: toNullableInteger(newCustomer.value.vehicle.modelYear),
        category: null,
        seats: null,
        bodyStyle: null,
        transmission: null,
        fuelType: toNullableString(newCustomer.value.vehicle.fuelType),
        colorInterior: null,
        previousOwners: null,
        plateChangeCount: null,
        regCode: null,
        trim: null,
        engineBadge: null,
        engineDisplacement: null,
        engineGeneration: null,
        powerBadge: null,
        enginePs: null,
        engineKw: null,
        batteryCapacity: null,
        aspiration: null,
        transmissionBadge: null,
        gears: null,
        drivetrain: null,
        drivetrainBadge: null,
        modelYearStart: null,
        modelYearEnd: null,
        lengthVariant: null
      },
      importInfo: {
        dealershipId: toNullableInteger(newCustomer.value.importInfo.dealershipID),
        source: toNullableString(newCustomer.value.importInfo.source),
        financePlanNumber: toNullableString(newCustomer.value.importInfo.financePlanNumber)
      },
      agreement: {
        productType: toNullableString(newCustomer.value.agreement.productType),
        productName: toNullableString(newCustomer.value.agreement.productName),
        startDate: toNullableString(newCustomer.value.agreement.startDate),
        endDate: toNullableString(newCustomer.value.agreement.endDate),
        term: toNullableInteger(newCustomer.value.agreement.term),
        rate: toNullableNumber(newCustomer.value.agreement.rate),
        rateType: toNullableString(newCustomer.value.agreement.rateType),
        deposit: toNullableNumber(newCustomer.value.agreement.deposit),
        purchasePrice: toNullableNumber(newCustomer.value.agreement.purchasePrice),
        odometer: {
          atStart: null,
          current: null,
          annualAllowance: toNullableInteger(newCustomer.value.agreement.odometer.annualAllowance),
          distanceUnit: toNullableString(newCustomer.value.agreement.odometer.distanceUnit)
        },
        monthlyPayment: toNullableNumber(newCustomer.value.agreement.monthlyPayment),
        equity: toNullableNumber(newCustomer.value.agreement.equity),
        amountBorrowed: toNullableNumber(newCustomer.value.agreement.amountBorrowed),
        finalRepayment: toNullableNumber(newCustomer.value.agreement.finalRepayment),
        settlement: null,
        latestValuationData: null,
        salesPerson: null,
        isCompleted: false,
        countryCode: addressCountryCode,
        currencyCode: toNullableString(newCustomer.value.agreement.currency)
      }
    }

    try {
      const response = await postNewCustomer.send({
        method: 'POST',
        endpoint: '/v1/customers',
        data: JSON.stringify(dataToPost)
      })

      if (response.success) {
        const agreementId = response?.data?.id

        if (!agreementId) {
          alertToast(
            'Customer added, but agreement redirect failed',
            'No agreement ID was returned by the API',
            'error'
          )
          return
        }

        // Emit completion to parent view
        router.push('/agreement/' + agreementId)
      } else {
        alertToast('Failed to add customer', response.message, 'error')
      }
    } catch (error) {
      alertToast('Failed to add customer', error.message || 'Unknown error', 'error')
    }
  }
}
</script>

<style></style>
